import {useEffect, useState, useRef} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Card from './Card';
import FilterBar from "./FilterBar";
import Lightbox from './Lightbox';
import Popup from './Popup';

const Home = () => {
    const { id: recallId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [slowLoading, setSlowLoading] = useState(false);
    const [showRetry, setShowRetry] = useState(false);
    const slowLoadingTimer = useRef(null);
    const retryButtonTimer = useRef(null);
    const abortControllerRef = useRef(null);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSources, setSelectedSources] = useState([]);
    const [/* totalItems */, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [lightboxImage, setLightboxImage] = useState(null);
    const [activeRecall, setActiveRecall] = useState(null);
    const [recallNotFound, setRecallNotFound] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL;

    const getData = (
        currentPage = page,
        limit = 3,
        search = searchTerm,
        sources = selectedSources,
        start = startDate,
        end = endDate
    ) => {
        setLoading(true);
        setError(false);

        const isFirstLoad = currentPage === 1 && data.length === 0;
        if (isFirstLoad) {
            setSlowLoading(false);
            setShowRetry(false);
            slowLoadingTimer.current = setTimeout(() => {
                setSlowLoading(true);
            }, 6000);
            retryButtonTimer.current = setTimeout(() => {
                setShowRetry(true);
            }, 30000);
        }

        // Cancel any still-pending request (e.g. an early manual retry) so it
        // can't clobber this attempt's state when it eventually settles.
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const queryParams = new URLSearchParams({
            page: currentPage,
            limit,
            search,
            sources: sources.join(","),
            ...(start && { startDate: start }),
            ...(end && { endDate: end })
        });

        axios.get(`${API_URL}/recalls?${queryParams}`, {
            headers: { 'x-api-key': process.env.REACT_APP_SECRET_API_KEY },
            timeout: 60000,
            signal: controller.signal
        }).then(res => {
            clearTimeout(slowLoadingTimer.current);
            clearTimeout(retryButtonTimer.current);
            setSlowLoading(false);
            setShowRetry(false);
            setData(prevData => currentPage === 1 ? res.data.recalls : [...prevData, ...res.data.recalls])//Append new results
            setPage(currentPage)
            setLoading(false);
            setTotalItems(res.data.totalItems)
            setTotalPages(res.data.totalPages)
            /* console.log(res.data) */
        }).catch(err => {
            if (axios.isCancel(err)) {
                return; // superseded by a newer retry, ignore
            }
            console.error("Failed to fetch recalls:", err);
            clearTimeout(slowLoadingTimer.current);
            clearTimeout(retryButtonTimer.current);
            setSlowLoading(false);
            setShowRetry(false);
            setLoading(false);
            setError(true);
        });
    };

    useEffect(() => { getData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

    useEffect(() => {
        return () => {
            clearTimeout(slowLoadingTimer.current);
            clearTimeout(retryButtonTimer.current);
            abortControllerRef.current?.abort();
        };
    }, []);

    const handleScroll = () => {
        // Load more when scrolled near bottom
        const isNearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 300;
        
        if (isNearBottom && page < totalPages && !loading) {
            getData(page + 1, 3);
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, false);

        // The initial page (or a filtered result set) may not fill the
        // viewport at all, in which case no scrollbar appears and the
        // 'scroll' event that would normally trigger the next page never
        // fires. Check right away so short pages still load more.
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll, false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, totalPages, loading]);

    useEffect(() => {
        if (!recallId) {
            setActiveRecall(null);
            setRecallNotFound(false);
            return;
        }

        const loaded = data.find(item => item._id === recallId);
        if (loaded) {
            setActiveRecall(loaded);
            setRecallNotFound(false);
            return;
        }

        // Not among the currently loaded pages (e.g. someone opened a
        // shared link directly) — fetch that one recall by itself.
        let cancelled = false;
        setActiveRecall(null);
        setRecallNotFound(false);
        axios.get(`${API_URL}/recalls/${recallId}`, {
            headers: { 'x-api-key': process.env.REACT_APP_SECRET_API_KEY }
        }).then(res => {
            if (!cancelled) {
                setActiveRecall(res.data);
            }
        }).catch(err => {
            if (!cancelled) {
                console.error("Failed to fetch recall:", err);
                setRecallNotFound(true);
            }
        });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recallId, data]);

    const openDetails = (item) => navigate(`/recall/${item._id}`);
    const closeDetails = () => navigate('/');

    const getRecallDescription = (recall) => {
        const plainText = (recall.content || (recall.articleContentHTML || '').replace(/<[^>]+>/g, ' '))
            .replace(/\s+/g, ' ')
            .trim();
        return plainText.slice(0, 160) || 'Katso tarkemmat tiedot Takaisinvedot-sivustolta.';
    };

    const getRecallImageUrl = (recall) => {
        if (recall.images?.[0]?.src) return recall.images[0].src;
        if (recall.details?.images?.[0]?.url) return recall.details.images[0].url;
        return `${window.location.origin}/og-image.png`;
    };

    const canonicalUrl = recallId
        ? `${window.location.origin}/recall/${recallId}`
        : `${window.location.origin}/`;

    const clearFilters = () => {
        setSearchTerm("");
        setSelectedSources([]);
        setStartDate("");
        setEndDate("");
        getData(1, 3, "", [], "", "");
    };

    return (
        <div>
            <Helmet>
                <title>{activeRecall ? `${activeRecall.title} – Takaisinvedot` : 'Takaisinvedot – Suomen tuotteiden takaisinvedot'}</title>
                <meta
                    name="description"
                    content={activeRecall
                        ? getRecallDescription(activeRecall)
                        : 'Kaikki Suomen tuotteiden takaisinvedot ja turvallisuustiedotteet yhdessä paikassa. Seuraa Ruokaviraston ja Tukesin takaisinvetoja ja saa ilmoitukset uusista vedoista suoraan puhelimeesi.'}
                />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={activeRecall ? activeRecall.title : 'Takaisinvedot'} />
                <meta property="og:url" content={canonicalUrl} />
                {activeRecall && <meta property="og:image" content={getRecallImageUrl(activeRecall)} />}
            </Helmet>

            {/* <UpdateButton onUpdated={() => getData(1)} />
            <DeleteButton onDeleted={() => getData(1)} /> */}

            <div className='head-title'>
                <img src='/banneri.png' alt='Takaisinvedot' className='banner-image' />
            </div>

                <main className={`${isFilterVisible ? 'filter-open' : ''} ${loading && data.length === 0 ? 'loading-empty' : ''}`}>
                    {/* Cards */}
                    <div className="card-container">
                        {data.map((item, index) => (
                            <Card
                                key={index}
                                item={item}
                                onImageClick={setLightboxImage}
                                onOpenDetails={openDetails}
                            />
                        ))}
                    </div>

                    <div className='center-div'>

                        {/* Initial loading spinner */}
                        {loading && data.length === 0 &&
                            <div className="loading-status">
                                <div className="spinner"></div>
                                <p className="loading-message">
                                    {slowLoading
                                        ? "Herätellään palvelinta, tämä voi kestää hetken..."
                                        : "Haetaan takaisinvetoja..."}
                                </p>
                                {showRetry && (
                                    <button
                                        className="clear-button"
                                        type="button"
                                        onClick={() => getData(1)}
                                    >
                                        Yritä uudelleen
                                    </button>
                                )}
                            </div>
                        }

                        {/* Loading next page */}
                        {loading && data.length > 0 && (
                            <div className="loading-status load-more-status">
                                <div className="spinner spinner-small"></div>
                                <p className="loading-message">Ladataan lisää...</p>
                            </div>
                        )}

                        {/* Load more indicator (idle, waiting for scroll) */}
                        {page < totalPages && !loading && data.length > 0 && (
                            <div className="load-more-status">
                                <p className="loading-message">Selaa alas ladataksesi lisää</p>
                            </div>
                        )}

                        {/* Error message */}
                        {error &&
                        <div className="error-block">
                            <h2>Takaisinvetoja ei voitu hakea</h2>
                            <button
                                className="clear-button"
                                type="button"
                                onClick={() => getData(1)}
                            >
                                Yritä uudelleen
                            </button>
                        </div>
                        }

                        {/* No results message */}
                        {!loading && data.length === 0 && !error &&
                        <div>
                            <h2>Ei tuloksia</h2>
                        </div>
                        }
                    
                    </div>                  
                </main>
            
            {/* Footer */}
            <FilterBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedSources={selectedSources}
                setSelectedSources={setSelectedSources}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                onSearch={() => getData(1, 3, searchTerm, selectedSources, startDate, endDate)}
                onClear={clearFilters}
                isVisible={isFilterVisible}
                setIsVisible={setIsFilterVisible}
            />

            {/* Open Filter Button (magnifying glass) */}
            <button
                className="open-filter-button"
                onClick={() => setIsFilterVisible(true)}
                aria-label="Avaa suodatin"
                type="button"
                style={{ display: isFilterVisible ? 'none' : 'flex' }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
            </button>
            
            {/* Lightbox */}
            <Lightbox imageUrl={lightboxImage} onClose={() => setLightboxImage(null)} />

            {/* Recall details popup, deep-linkable via /recall/:id */}
            <Popup
                isOpen={!!recallId}
                data={activeRecall}
                notFound={recallNotFound}
                closePopup={closeDetails}
                onImageClick={setLightboxImage}
            />
        </div>
    )
}

export default Home;